import type { FC } from 'react';
import { useMemo } from 'react';
// @mui
import {
  Box,
  Button,
  Card,
  CardHeader,
  styled,
  Table,
  TableBody,
  TableCell as TableCellOriginal,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { GetDashboardResult } from '@pflegenavi/frontend/api-nursing-home';
import { useFormatting } from '@pflegenavi/frontend/localization';
import type { TabFilterValues } from '@pflegenavi/frontend/pages/residents';
import { useNavigateToResidentsPage } from '@pflegenavi/frontend/pages/residents';
import { generatePath, nhAppMainPages } from '@pflegenavi/frontend/routing';
import {
  Iconify,
  OverdueDaysLabel,
  TableEmptyRows,
  TableNoData,
} from '@pflegenavi/web-components';
import { differenceInDays } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

const TableCell = styled(TableCellOriginal)`
  padding: 12px;
`;

// ----------------------------------------------------------------------

export interface BalanceTableProps {
  data: GetDashboardResult['data']['negative_balance']['residents'];
  cardTitle: string;
  negative: boolean;
  navigateTo: TabFilterValues;
  dataCyPrefix: string;
  noDataImg: string;
  noDataText: string;
}

export const BalanceCard: FC<BalanceTableProps> = ({
  data,
  cardTitle,
  negative,
  navigateTo,
  dataCyPrefix,
  noDataImg,
  noDataText,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { fCurrencyCents } = useFormatting();
  const { navigateToResidentPage } = useNavigateToResidentsPage();

  const handleViewAll = () => {
    navigateToResidentPage({
      selectedTab: navigateTo,
    });
  };

  const sortedData = useMemo(() => {
    return data.sort(sortBalanceOverdue);
  }, [data]);

  return (
    <Card
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minWidth: {
          xs: '100%',
          md: 500,
        },
      }}
      data-cy={dataCyPrefix}
    >
      <CardHeader
        title={cardTitle}
        sx={{ mb: 3 }}
        slotProps={{
          title: {
            variant: 'h5',
            sx: { fontWeight: 600 },
          },
        }}
      />
      <TableContainer
        sx={{
          maxWidth: '96%',
          marginLeft: 'auto',
          marginRight: 'auto',
        }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('dashboard.table.resident')}</TableCell>
              <TableCell align="right">{t('dashboard.table.amount')}</TableCell>
              <TableCell align="center">
                {t('dashboard.table.daysOverdue')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.length === 0 && (
              <TableNoData colSpan={3} isNotFound>
                <img
                  alt="empty content"
                  src={noDataImg}
                  style={{ marginBottom: 25 }}
                  height={180}
                  width={220}
                />
                <Typography
                  variant="inherit"
                  sx={{
                    fontWeight: '700',
                    mb: 2,
                  }}
                >
                  {noDataText}
                </Typography>
              </TableNoData>
            )}

            {sortedData.map((row, index) => {
              const daysOverdue = differenceInDays(new Date(), row.due_date);
              const onClick = () => {
                navigate(
                  generatePath(nhAppMainPages.RESIDENT_DETAIL_ACCOUNT, {
                    resident_id: row.id,
                  })
                );
              };
              return (
                <TableRow
                  hover
                  sx={{
                    borderBottomStyle: 'solid',
                    borderBottomColor: theme.palette.grey[500_24],
                    borderBottomWidth: 'thin',
                    cursor: 'pointer',
                  }}
                  key={index}
                  onClick={onClick}
                >
                  <TableCell align="left">
                    <Typography variant="subtitle2" noWrap>
                      {row.firstname} {row.lastname}
                    </Typography>
                  </TableCell>

                  <TableCell align="right">
                    {fCurrencyCents(row.balance)}
                  </TableCell>

                  <TableCell align="center">
                    <OverdueDaysLabel
                      negative={negative}
                      daysOverdue={daysOverdue}
                    />
                  </TableCell>
                </TableRow>
              );
            })}

            {sortedData && sortedData.length > 0 && (
              <TableEmptyRows
                height={48}
                emptyRows={tableMaxRows - sortedData.length}
              />
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {data?.length > 0 && (
        <Box
          sx={{
            p: 2,
            textAlign: 'right',
            mt: 'auto',
          }}
        >
          <Button
            data-cy={`${dataCyPrefix}-view-all-button`}
            size="small"
            color="inherit"
            endIcon={<Iconify icon={'eva:arrow-ios-forward-fill'} />}
            onClick={handleViewAll}
          >
            {t('dashboard.label.viewAll')}
          </Button>
        </Box>
      )}
    </Card>
  );
};

const sortBalanceOverdue = (
  a: GetDashboardResult['data']['negative_balance']['residents'][number],
  b: GetDashboardResult['data']['negative_balance']['residents'][number]
): number => {
  const dateA = new Date(a.due_date).getTime();
  const dateB = new Date(b.due_date).getTime();
  const balanceA = a.balance;
  const balanceB = b.balance;

  if (dateA < dateB) {
    return -1;
  } else if (dateB < dateA) {
    return 1;
  } else {
    if (balanceA < balanceB) {
      return -1;
    } else if (balanceB < balanceA) {
      return 1;
    } else {
      return 0;
    }
  }
};

const tableMaxRows = 5;
